import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
} from "react-native";
import React, { useState, useEffect, useRef } from "react";

import ClientSummary from "./Summary/ClientSummary";
import ClientPlanification from "./Planification/ClientPlanification";
import ClientCalendar from "./Calendar/ClientCalendar";
import ClientStatistics from "./Statistics/ClientStatistics";
import ClientProfile from "./Profile/ClientProfile";

import Colors from "../../Resources/Color";
import GlobalStyles from "../../Resources/Styles";

import { observer, Observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "./../_layout";
import ClientForms from "./Forms/ClientForms";

import { useTranslation } from "react-i18next";
import CustomActivityIndicator from "../Components/CustomActivityIndicator";
import SyncStatusIndicator from "../Components/SyncStatusIndicator";
import { SyncStatus } from "../../Resources/Constants";
import LogoImage from "../Components/LogoImage";
import ClientNutrition from "./Nutrition/ClientNutrition";

const TabBar = React.memo(
  ({
    client,
    selectedTab,
    t,
    isMobile,
    screenSize,
    TabOptions,
    hasUnseenForms,
  }) => {
    const scrollViewRef = useRef(null);

    const TabButton = ({ option }) => {
      const [isHovered, setIsHovered] = useState(false);

      // Check if this is the forms tab and if there are unseen forms
      const showBadge = option === "forms-string" && hasUnseenForms;

      return (
        <View
          style={{
            flexDirection: "row",
            marginRight: screenSize === "small" ? 12 : 24,
          }}
        >
          <TouchableOpacity
            onPress={() => client.setProperty("selectedTab", option)}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View style={{ alignItems: "center" }}>
              <View style={{ flexDirection: "row", alignItems: "center" }}>
                <Text
                  style={[
                    styles.tabText,
                    {
                      color: "black",
                    },
                  ]}
                >
                  {t(option)}
                </Text>
                {showBadge && (
                  <View
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: Colors.bubbleColor,
                      marginLeft: 6,
                    }}
                  />
                )}
              </View>
              <View
                style={[
                  styles.underline,
                  {
                    backgroundColor:
                      selectedTab === option
                        ? Colors.bubbleColor
                        : isHovered
                        ? "rgba(63, 123, 141, 0.65)"
                        : "transparent",
                  },
                ]}
              />
            </View>
          </TouchableOpacity>
          <View style={{ flex: 1 }} />
        </View>
      );
    };

    return (
      <>
        <View style={{}}>
          <ScrollView
            ref={scrollViewRef}
            horizontal={true}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              marginTop: 30,
            }}
          >
            <View style={{ flexDirection: "row" }}>
              {TabOptions.map((option, index) => (
                <TabButton option={option} key={index} />
              ))}
            </View>
          </ScrollView>
        </View>
        <View
          style={{
            maxHeight: 1,
            flex: 1,
            backgroundColor: Colors.borderColor,
          }}
        />
      </>
    );
  }
);

const ClientDashboard = observer(({ client }) => {
  const { t } = useTranslation();
  const { isMobile } = useDevice();

  // Check if there are unseen forms (needs to be in observer component for reactivity)
  const hasUnseenForms = client.forms.some((form) => !form.getSeenByCoach);

  const TabOptions = client.allowedNutrition
    ? [
        "summary-string",
        "planification-string",
        "calendar-string",
        "forms-string",
        "stats-string",
        "nutrition-string",
        "profile-string",
      ]
    : [
        "summary-string",
        "planification-string",
        "calendar-string",
        "forms-string",
        "stats-string",
        "profile-string",
      ];
  const components = {
    "summary-string": ClientSummary,
    "planification-string": ClientPlanification,
    "calendar-string": ClientCalendar,
    "forms-string": ClientForms,
    "stats-string": ClientStatistics,
    "nutrition-string": ClientNutrition,
    "profile-string": ClientProfile,
  };
  const ComponentToShow = components[client.selectedTab] || null;

  const { screenSize } = useScreenDimensions();

  console.log("clientDashboard refreshed");

  useEffect(() => {
    return () => {
      console.log("ClientDashboard unmounted", client.name);
      if (client?.coach?.selectedChatId != client.id) {
        client.firestoreListener.removeBlocksListener();
        client.syncStatusManager.setOperationStatus("listenerBlocks", null);
      }

      client.firestoreListener.removeFormsListener();
      client.syncStatusManager.setOperationStatus("listenerForms", null);
    };
  }, [client]);

  return (
    <>
      {client.isLoading ? (
        <CustomActivityIndicator />
      ) : (
        <View style={[styles.container, { flex: 1 }]}>
          <View style={{ height: "100%" }}>
            {client && (
              <View
                style={[
                  GlobalStyles.HStack,
                  {
                    justifyContent: "space-between",
                    alignItems: "center",
                  },
                ]}
              >
                <View>
                  <View
                    style={[
                      GlobalStyles.HStack,
                      {
                        width: "100%",
                        justifyContent: "flex-start",
                      },
                    ]}
                  >
                    <Observer>
                      {() => (
                        <Text
                          style={[
                            styles.title,
                            {
                              paddingRight: screenSize === "small" ? 8 : 16,
                              maxWidth: "90%",
                              flexShrink: 1,
                            },
                          ]}
                          numberOfLines={1}
                        >
                          {client.nameForCoach}
                        </Text>
                      )}
                    </Observer>
                    <SyncStatusIndicator
                      syncStatusManager={client.syncStatusManager}
                      onPress={() => client.performTransaction()}
                    />
                  </View>
                  <Text
                    style={[styles.subtitle, { fontSize: 14, marginTop: 4 }]}
                  >
                    {client.email}
                  </Text>
                </View>
                {client.coach && client.coach.logoUrl && (
                  <LogoImage coach={client.coach} />
                )}
              </View>
            )}

            <TabBar
              client={client}
              selectedTab={client.selectedTab}
              t={t}
              isMobile={isMobile}
              screenSize={screenSize}
              TabOptions={TabOptions}
              hasUnseenForms={hasUnseenForms}
            />

            {ComponentToShow && <ComponentToShow client={client} />}
          </View>
        </View>
      )}
    </>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: "750",
  },
  subtitle: {
    fontSize: 18,
    fontWeight: "500",
    color: "gray",
  },
  tabText: {
    fontSize: 16,
    fontWeight: "400",
  },
  underline: {
    height: 2,
    width: "100%",
    alignSelf: "center",
    marginTop: 5,
  },
});

export default ClientDashboard;
